import React, { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Questionnaires from 'src/components/Questionnaires/Questionnaires';
import { Tabs } from '@ds/elements/Tabs/Tabs';
import { useGetCaseDetails } from 'src/shared/queries/useGetCaseDetails';
import BreadCrumb from '@ds/elements/BreadCrumb/BreadCrumb';
import folderIcon from '@icons/folder-filled.svg';
import { AsyncDataHandler, Tag } from '@ds';
import { TagVariant } from '@ds/elements/Tag/Tag.types';
import { useGetCaseQuestionnaires } from 'src/shared/queries/useGetCaseQuestionnaires';
import CaseDocument from './CaseDocumentsTabs/CaseDocument/CaseDocument';
import { CaseDetails } from 'src/components/CaseDetails/CaseDetails';
import CaseTimeline from 'src/components/CaseTimeline/CaseTimeline';
import { CaseDocumentsTabs } from 'src/utils/constants/Case';
import { PassportClickInterceptor } from 'src/components/PassportClickInterceptor/PassportClickInterceptor';
import { useNavigateWithSecureParams } from 'src/utils/hooks/useNavigateWithSecureParams';
import { useGetUserContacts } from 'src/shared/queries/useGetUserContacts';
import { useGetUser } from 'src/shared/queries/useGetUser';
import { AddressType } from 'src/shared/types/User';
import { useGetCaseList } from 'src/shared/queries/useGetCaseList';
import FullScreenLoading from 'src/components/FullScreenLoading/FullScreenLoading';
import styles from './CaseDocuments.module.scss';

const CaseDocuments: React.FC = () => {
  const { navigate, getParam, setParam, removeParam } = useNavigateWithSecureParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: userProfile } = useGetUser();
  const { data: userContacts } = useGetUserContacts(userProfile?.userId, true);
  const workLocations =
    userContacts?.contactAddresses?.filter((address) => address.addressType === AddressType.WORK) || [];
  const caseIdFromUrl = searchParams.get('caseId');
  const caseIdFromParams = getParam('caseId');
  const caseId = caseIdFromUrl || caseIdFromParams;
  const numericCaseId = caseId && !isNaN(Number(caseId)) ? Number(caseId) : undefined;
  const {
    data: caseDetails,
    isLoading: isLoadingCaseDetails,
    error: caseError,
  } = useGetCaseDetails(numericCaseId ?? -1);
  const isActive = caseDetails?.isActive ?? false;
  const { data: questionnaires, isLoading: isLoadingQuestionnaires } = useGetCaseQuestionnaires(
    isActive && numericCaseId ? numericCaseId : 0
  );
  const tabNameFromUrl = searchParams.get('tab');
  const tabParam = getParam('tab');

  const isValidTabNameFromUrl =
    tabNameFromUrl && Object.values(CaseDocumentsTabs).includes(tabNameFromUrl as CaseDocumentsTabs);
  const currentTab = (isValidTabNameFromUrl ? tabNameFromUrl : tabParam) || CaseDocumentsTabs.CaseDocuments;
  const caseName = caseDetails?.caseType || caseDetails?.caseName || '';
  const caseGUIDFromUrl = searchParams.get('caseGUID');
  const caseGUIDParam = getParam('caseGUID');
  // Use caseGUID from URL or secure params - URL takes precedence for incoming redirects
  const effectiveCaseGUID = caseGUIDFromUrl || caseGUIDParam;
  const { data: casesList, isLoading: loadingCasesList } = useGetCaseList(
    !!effectiveCaseGUID ? userProfile?.beneId : 0,
    true
  );

  useEffect(() => {
    const newParams = new URLSearchParams(searchParams);
    let shouldUpdateParams = false;

    if (isValidTabNameFromUrl) {
      setParam('tab', tabNameFromUrl);
      newParams.delete('tab');
      shouldUpdateParams = true;
    }

    if (caseIdFromUrl) {
      setParam('caseId', caseIdFromUrl);
      newParams.delete('caseId');
      shouldUpdateParams = true;
    }

    if (shouldUpdateParams) {
      setSearchParams(newParams, { replace: true });
    }
  }, [tabNameFromUrl, isValidTabNameFromUrl, caseIdFromUrl, searchParams, setSearchParams, setParam]);

  // Get caseGUID from search params and then remove it from the URL
  useEffect(() => {
    const caseGUID = searchParams.get('caseGUID');
    if (caseGUID) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('caseGUID');
      setParam('caseGUID', caseGUID);

      // Update the URL without the caseGUID parameter, but keep other parameters
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setParam, setSearchParams]);

  useEffect(() => {
    // Use effectiveCaseGUID which includes both URL and secure params
    if (!!effectiveCaseGUID && !!casesList) {
      const selectedCase = casesList.find((caseItem) => caseItem.universalCaseId === effectiveCaseGUID);
      // Clean up both URL and secure params
      if (caseGUIDFromUrl) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('caseGUID');
        setSearchParams(newParams, { replace: true });
      }
      removeParam('caseGUID');
      if (selectedCase) {
        const tab = selectedCase.isActive ? undefined : CaseDocumentsTabs.Timeline;
        navigate('/case-documents', { secureParams: { caseId: selectedCase.caseId, tab } });
      } else {
        // Case not found in list - could be a remote case or data mismatch
        // Try to find case by matching the caseGUID more flexibly (case-insensitive comparison)
        const selectedCaseFallback = casesList.find(
          (caseItem) => caseItem.universalCaseId?.toLowerCase() === effectiveCaseGUID?.toLowerCase()
        );
        if (selectedCaseFallback) {
          const tab = selectedCaseFallback.isActive ? undefined : CaseDocumentsTabs.Timeline;
          navigate('/case-documents', { secureParams: { caseId: selectedCaseFallback.caseId, tab } });
        } else {
          // If still not found, navigate to cases page - the case might not be accessible
          console.warn('Case not found for caseGUID:', effectiveCaseGUID, 'Available cases:', casesList.map(c => ({ id: c.caseId, guid: c.universalCaseId })));
          navigate('/cases');
        }
      }
    }
  }, [effectiveCaseGUID, caseGUIDFromUrl, casesList, removeParam, navigate, searchParams, setSearchParams]);

  // Use the original caseId string for navigation, fallback to empty string if missing
  const changeTab = (tabName: CaseDocumentsTabs) => {
    navigate(`/case-documents?tab=${tabName}`, {
      secureParams: {
        caseId: caseId || '',
        tab: tabName,
      },
    });
  };

  const tabs = [
    ...(isActive
      ? []
      : [
          {
            header: 'Timeline',
            content: (
              <div>
                <AsyncDataHandler isLoading={isLoadingCaseDetails} error={caseError}>
                  {caseDetails && (
                    <>
                      <CaseDetails caseDetails={caseDetails} workLocations={workLocations} />
                      <CaseTimeline caseDetails={caseDetails} />
                    </>
                  )}
                </AsyncDataHandler>
              </div>
            ),
            visible: true,
            tabName: CaseDocumentsTabs.Timeline,
          },
        ]),
    {
      header: 'Case Documents',
      content: (
        <div>
          <AsyncDataHandler isLoading={isLoadingCaseDetails} error={caseError}>
            {caseDetails && <CaseDocument caseDetails={caseDetails} />}
          </AsyncDataHandler>
        </div>
      ),
      tabName: CaseDocumentsTabs.CaseDocuments,
      visible: true,
    },
    ...(isActive
      ? [
          {
            header: <PassportClickInterceptor>Questionnaire</PassportClickInterceptor>,
            content: (
              <div data-leave-modal-content>
                <AsyncDataHandler isLoading={isLoadingQuestionnaires} loader={{ width: '100%', height: '32px' }}>
                  {questionnaires && questionnaires.length > 0 && caseDetails && (
                    <Questionnaires caseId={caseDetails?.caseId} questionnaires={questionnaires} outline={false} />
                  )}
                  {!isLoadingQuestionnaires && questionnaires && questionnaires.length == 0 && (
                    <div className={styles['no-questionnaires']}>
                      <p className={styles['empty-message-text']}>
                        No questionnaire has been assigned to you yet. When questionnaires are assigned, it will be
                        populated here
                      </p>
                    </div>
                  )}
                </AsyncDataHandler>
              </div>
            ),
            tabName: CaseDocumentsTabs.Questionnaire,
          },
        ]
      : []),
  ];

  const breadcrumbItems = [
    { label: 'Cases', url: '/cases' },
    { label: caseName, url: `${location.pathname}${location.search}` },
  ];

  const caseSubTypes = useMemo(() => {
    const associatedCaseSubTypes = caseDetails?.associatedCaseSubTypes;
    if (!associatedCaseSubTypes) {
      return null;
    }
    return associatedCaseSubTypes.map(({ caseSubType }: { caseSubType: string }) => caseSubType).join(', ');
  }, [caseDetails?.associatedCaseSubTypes]);

  const caseContent = ' • ' + caseSubTypes + ' • ' + caseDetails?.caseCountry + ' • ' + caseDetails?.balNumber;

  if (loadingCasesList) {
    return <FullScreenLoading />;
  }

  return (
    <AsyncDataHandler isLoading={isLoadingCaseDetails} error={caseError} loader={{ width: '100%' }}>
      <PassportClickInterceptor>
        <BreadCrumb items={breadcrumbItems} />
        <div className={styles['case-details-header']}>
          <div className={styles['left-container']}>
            <img src={folderIcon} className={styles['icon']} />
            <p className={styles['case-type']}>{caseName} Case</p>
            {!isActive && <Tag variant={TagVariant.Info}>CLOSED</Tag>}
          </div>
          <div className={styles['right-container']}>
            <p>{caseContent}</p>
          </div>
        </div>
        {/* Pass the tabName to the Tabs component */}
        <Tabs
          className={styles['case-documents-tabs']}
          tabs={tabs}
          activeTabIndex={tabs.findIndex((tab) => tab.tabName === currentTab)}
          onTabChange={(index) => changeTab(tabs[index].tabName ?? CaseDocumentsTabs.CaseDocuments)}
        ></Tabs>
      </PassportClickInterceptor>
    </AsyncDataHandler>
  );
};

export default CaseDocuments;
